import { ProcessTriggerDetailDto } from '@/api/backoffice/clients';
import { computed, ref } from 'vue';
import { object, string, mixed, boolean, bool, ValidationError } from 'yup';

export const INVALID_CRON_SCHEDULE = 'Invalid cron schedule';

export const useProcessTriggerValidations = (t, detail: ProcessTriggerDetailDto = null) => {
    const validationSchema = object({
        triggerType: object().required(
            t('validations.messages.required', { _field_: t('processtrigger.detail.triggerType') })
        ),
        description: string().required(
            t('validations.messages.required', { _field_: t('processtrigger.detail.description') })
        ),
        deliveryProcessDefinition: object().nullable(),
        productProcessDefinition: object().nullable(),
        validCronSchedule: bool().nullable().default(true),
        isEnabled: boolean().required(
            t('validations.messages.required', { _field_: t('processtrigger.detail.isEnabled') })
        ),
        custom: mixed().test(
            'custom-validation',
            'The properties do not meet the validation criteria',
            function (_, context) {
                const {
                    typeTriggerType,
                    deliveryProcessDefinition,
                    productProcessDefinition,
                    triggerDeliveryProcessDefinition,
                    triggerProductProcessDefinition,
                    validCronSchedule,
                    schedule
                } = context.parent;

                const inner: ValidationError[] = [];

                if (!typeTriggerType) {
                    const msg =
                        triggerTypeRef.value?.id === 1
                            ? t('validations.messages.required', { _field_: t('processtrigger.detail.deliveryTriggerType') })
                            : triggerTypeRef.value?.id === 2
                            ? t('validations.messages.required', { _field_: t('processtrigger.detail.productTriggerType') })
                            : t('validations.messages.required', { _field_: t('processtrigger.detail.timeTriggerType') });

                    inner.push(new ValidationError(msg, undefined, 'typeTriggerType'));
                }

                if (typeTriggerType?.name == 'Levering' && !deliveryProcessDefinition) {
                    inner.push(
                        new ValidationError(
                            t('validations.messages.required', { _field_: t('processtrigger.detail.deliveryProcessDefinition') }),
                            undefined,
                            'deliveryProcessDefinition'
                        )
                    );
                }

                if (typeTriggerType?.name == 'Product' && !productProcessDefinition) {
                    inner.push(
                        new ValidationError(
                            t('validations.messages.required', { _field_: t('processtrigger.detail.productProcessDefinition') }),
                            undefined,
                            'productProcessDefinition'
                        )
                    );
                }

                if (triggerTypeRef.value?.id === 1 || triggerTypeRef.value?.id === 2) {
                    if(!triggerDeliveryProcessDefinition && !triggerProductProcessDefinition){
                        if (typeTriggerType?.name == 'Levering' && !triggerDeliveryProcessDefinition) {
                            inner.push(
                                new ValidationError(
                                    t('validations.messages.required', { _field_: t('processtrigger.detail.triggerDeliveryProcessDefinition') }),
                                    undefined,
                                    'triggerDeliveryProcessDefinition'
                                )
                            );
                        }

                        if (typeTriggerType?.name == 'Product' && !triggerProductProcessDefinition) {
                            inner.push(
                                new ValidationError(
                                    t('validations.messages.required', { _field_: t('processtrigger.detail.triggerProductProcessDefinition') }),
                                    undefined,
                                    'triggerProductProcessDefinition'
                                )
                            );
                        }
                    } else{
                        if(deliveryProcessDefinition && triggerDeliveryProcessDefinition && deliveryProcessDefinition.id === triggerDeliveryProcessDefinition.id){
                            inner.push(new ValidationError(t('processtrigger.detail.duplicateDeliveryProcessDefinition'), undefined, 'triggerDeliveryProcessDefinition'));
                        } else if(productProcessDefinition && triggerProductProcessDefinition && productProcessDefinition.id === triggerProductProcessDefinition.id){
                            inner.push(new ValidationError(t('processtrigger.detail.duplicateProductrocessDefinition'), undefined, 'triggerProductProcessDefinition'));
                        }
                    }
                }

                if (triggerTypeRef.value?.id === 3 && !schedule) {
                    inner.push(
                        new ValidationError(
                            t('validations.messages.required', { _field_: t('processtrigger.detail.schedule') }),
                            undefined,
                            'schedule'
                        )
                    );
                }

                if (schedule && !validCronSchedule) {
                    inner.push(new ValidationError(t('processtrigger.detail.invalidCronExpression'), undefined, 'schedule'));
                }

                if (inner.length) {
                    // join inner messages into a single string for the aggregate message
                    const aggregate = new ValidationError(inner.map((e) => e.message).join('; '), undefined, undefined);
                    aggregate.inner = inner;
                    throw aggregate;
                }

                return true;
            }
        )
    });

    const triggerTypeRef = ref(detail?.triggerType);

    return {
        validationSchema,
        triggerTypeRef
    };
};
